<template>
  <el-select
    v-if="hasPredefinedOptions || hasRemoteOptions"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    placeholder=""
    size="small"
    :multiple="!isBooleanOperator"
    default-first-option
    :filterable="hasRemoteOptions"
    :remote="hasRemoteOptions"
    :remote-method="search"
    :value-key="hasRemoteOptions ? 'id' : undefined"
    :loading="isLoading"
    clearable
    class="w-100"
  >
    <template v-if="hasRemoteOptions">
      <el-option
        v-for="selectable in selectableOptions"
        :key="selectable.id"
        :label="$filters.truncate(selectable.label, 15)"
        :value="selectable"
      >
        {{ selectable.label }}
      </el-option>
      <!-- <el-option
        v-if="filterable.filter.label"
        :key="1"
        :label="$filters.titleize(filterable.filter.label)"
        :value="filterable.filter.value"
      >
        {{ filterable.filter.label }}
      </el-option> -->
    </template>

    <template v-else>
      <template v-if="Array.isArray(selectableOptions)">
        <el-option
          v-for="(value, index) in selectableOptions"
          :key="index"
          :label="$filters.titleize(value)"
          :value="value"
        />
      </template>

      <template v-else>
        <el-option
          v-for="(value, label) in selectableOptions"
          :key="`${label}_${value}`"
          :label="$filters.titleize(label)"
          :value="value"
        />
      </template>
    </template>
  </el-select>

  <el-input-number
    v-else-if="isNumberFieldType || isIntegerFieldType"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    controls-position="right"
    size="small"
    class="w-100"
    @keydown.enter.native.stop.prevent="$emit('apply')"
  />

  <el-time-select
    v-else-if="isTimeFieldType"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    :is-range="isBetweenOperator"
    format="HH:mm"
    value-format="HH:mm:ss"
    range-separator="To"
    start-placeholder="Start time"
    end-placeholder="End time"
    placeholder="Start time"
    size="small"
    class="w-100"
    @keydown.enter.native.stop.prevent="$emit('apply')"
  />

  <el-date-picker
    v-else-if="isDateFieldType"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    format="dd/MM/yyyy"
    value-format="yyyy-MM-dd"
    :type="(isBetweenOperator ? 'daterange' : 'date')"
    range-separator="To"
    start-placeholder="Start date"
    end-placeholder="End date"
    size="small"
    class="w-100"
    @keydown.enter.native.stop.prevent="$emit('apply')"
  />

  <el-date-picker
    v-else-if="isDateTimeFieldType"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    format="dd/MM/yyyy HH:mm"
    value-format="yyyy-MM-dd HH:mm:ss"
    :type="(isBetweenOperator ? 'datetimerange' : 'datetime')"
    range-separator="To"
    start-placeholder="Start date"
    end-placeholder="End date"
    size="small"
    class="w-100"
    @keydown.enter.native.stop.prevent="$emit('apply')"
  />

  <el-input
    v-else
    ref="textInput"
    v-model="filterable.filter.value"
    :disabled="isDisabled"
    clearable
    size="small"
    class="w-100"
    @keydown.enter.native.stop.prevent="$emit('apply')"
  />
</template>

<script>
import Resource from '@/plugins/sdk/Resource'
import {
  ARRAY,
  BOOLEAN,
  DATE,
  DATETIME,
  INTEGER,
  COLLECTION,
  NUMBER,
  OBJECT,
  ITEM,
  STRING,
  TIME,
  UUID
} from '@/plugins/sdk/fields'
import {
  IS_BETWEEN,
  IS_EMPTY,
  IS_FALSE,
  IS_NOT_BETWEEN,
  IS_TRUE
} from '@/plugins/sdk/operators'

export default {
  props: {
    resource: {
      type: [ Object, Function ],
      required: true
    },
    filterable: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      searchResults: []
    }
  },
  computed: {
    /**
     * Returns an instance of the resources field relative to the
     * filters active field.
     */
    resourceField () {
      let resource = this.resource
      let field

      if (typeof this.filterable.filter.field !== 'undefined') {
        this
          .filterable
          .filter
          .field
          .split('.')
          .map(f => {
            if (resource instanceof Resource) {
              field = resource.constructor.fields[f]
            } else if (resource.prototype instanceof Resource) {
              field = resource.fields[f]
            }

            resource = field._resource || resource
          })
      }
      return field
    },
    /**
     * Determines if the field type is array.
     */
    isArrayFieldType () {
      return this.resourceField instanceof ARRAY
    },
    /**
     * Determines if the field type is boolean.
     */
    isBooleanFieldType () {
      return this.resourceField instanceof BOOLEAN
    },
    /**
     * Determines if the field type is date.
     */
    isDateFieldType () {
      return this.resourceField instanceof DATE
    },
    /**
     * Determines if the field type is date time.
     */
    isDateTimeFieldType () {
      return this.resourceField instanceof DATETIME
    },
    /**
     * Determines if the field type is integer.
     */
    isIntegerFieldType () {
      return this.resourceField instanceof INTEGER
    },
    /**
     * Determines if the field type is collection.
     */
    isCollectionFieldType () {
      return this.resourceField instanceof COLLECTION
    },
    /**
     * Determines if the field type is number.
     */
    isNumberFieldType () {
      return this.resourceField instanceof NUMBER
    },
    /**
     * Determines if the field type is object.
     */
    isObjectFieldType () {
      return this.resourceField instanceof OBJECT
    },
    /**
     * Determines if the field type is item.
     */
    isItemFieldType () {
      return this.resourceField instanceof ITEM
    },
    /**
     * Determines if the field type is string.
     */
    isStringFieldType () {
      return this.resourceField instanceof STRING
    },
    /**
     * Determines if the field type is time.
     */
    isTimeFieldType () {
      return this.resourceField instanceof TIME
    },
    /**
     * Determines if the field type is uuid.
     */
    isUuidFieldType () {
      return this.resourceField instanceof UUID
    },
    /**
     * Determines if the selected operator is a between operator.
     */
    isBetweenOperator () {
      return this.instanceOfBetweenOperator(this.filterable.filter.operator)
    },
    /**
     * Determines if the selected operator is a boolean operator.
     */
    isBooleanOperator () {
      return this.instanceOfBooleanOperator(this.filterable.filter.operator)
    },
    /**
     * Determines if the selected operator is an empty operator.
     */
    isEmptyOperator () {
      return this.instanceOfEmptyOperator(this.filterable.filter.operator)
    },
    /**
     * Determines if the filterable defines options.
     */
    hasPredefinedOptions () {
      return this.isBooleanOperator || Object.keys(this.filterable.options).length > 0
    },
    /**
     * Determines if the filterable defines options.
     */
    hasRemoteOptions () {
      return this.isCollectionFieldType || this.isItemFieldType
    },
    /**
     * Returns the selectable options.
     */
    selectableOptions () {
      let options = []

      if (this.hasPredefinedOptions) {
        if (this.isBooleanOperator) {
          options = { 'Yes': 1, 'No': 0 }
        } else {
          options = this.filterable.options
        }
      } else if (this.hasRemoteOptions) {
        options = this.searchResults
      }

      if (options.length === 0 && this.filterable.filter.label && this.filterable.filter.value && this.filterable.filter.value[0]) {
        options = this.filterable.filter.value.map((value, index) => {
          return {
            label: this.filterable.filter.label[index],
            id: value.id
          }
        })
      }

      return options
    },
    /**
     * Determines if the input should be disabled
     */
    isDisabled () {
      return this.isEmptyOperator
    }
  },
  watch: {
    'filterable.filter.operator': function (newOperator, previousOperator) {
      let compatible = true

      if ((this.isBetweenOperator && !this.instanceOfBetweenOperator(previousOperator)) ||
        (this.isBooleanOperator && !this.instanceOfBooleanOperator(previousOperator)) ||
        (this.isEmptyOperator && !this.instanceOfEmptyOperator(previousOperator))) {
        compatible = false
      }

      if (!compatible) {
        this.filterable.filter.value = undefined
      }
    },
    'filterable.filter.value': function (newValue) {
      if (this.isBooleanFieldType) {
        this.filterable.filter.operator = (newValue ? IS_TRUE : IS_FALSE)
      }
    }
  },
  methods: {
    search (query) {
      if (query !== '') {
        this.isLoading = true

        this
          .$sdk
          .search(query, this.resourceField._resource)
          .then(result => { this.searchResults = result })
          .finally(() => { this.isLoading = false })
      }
    },
    instanceOfBetweenOperator (operator) {
      let is = false

      if (operator) {
        is = [
          IS_BETWEEN.operator,
          IS_NOT_BETWEEN.operator
        ].includes(operator.operator)
      }

      return is
    },
    instanceOfBooleanOperator (operator) {
      let is = false

      if (operator) {
        is = (this.$_.isEqual(IS_TRUE, operator) || this.$_.isEqual(IS_FALSE, operator))
      }

      return is
    },
    instanceOfEmptyOperator (operator) {
      let is = false

      if (operator) {
        is = (operator.operator === IS_EMPTY.operator)
      }

      return is
    }
  }
}
</script>
